import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./ProductEngineering.css";
import "./PageTheme.css";
import serviceImage from "../../../images/about/lab.jpg";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "Product Engineering",
  "category": "Validation, Automation & Product Engineering",
  "visual": "product",
  "folder": "ProductEngineering",
  "sections": [
    [
      "Product Engineering",
      "Engineering Products Ready for Real-World Deployment\nA successful semiconductor product is defined not only by reliable silicon but also by its ability to perform consistently within the complete system. Product Engineering bridges the gap between validated silicon and market-ready products by integrating hardware and software, optimizing system performance, verifying functionality, and preparing products for deployment.\n\nDigicomm Semiconductor delivers comprehensive Product Engineering services that support the final stages of product development. From board bring-up and system integration to product verification, performance optimization, qualification support, and production readiness, our engineering teams help customers transform innovative designs into dependable products through disciplined execution and collaborative engineering.\n\nPrimary CTA\n\nDiscuss Your Product Engineering Requirements"
    ],
    [
      "Product Engineering",
      "Product Engineering\nProduct Engineering brings together hardware, software, and system-level engineering to transform validated silicon into a reliable, deployable product. It focuses on integrating system components, optimizing performance, verifying functionality, and preparing products to operate consistently in their intended application. This phase plays a critical role in ensuring that engineering objectives translate into dependable real-world performance.\n\nDigicomm Semiconductor provides comprehensive Product Engineering services that support the final stages of semiconductor product development. Our engineering teams work closely with customers to integrate hardware and software, perform board bring-up, validate system functionality, optimize product performance, support qualification activities, and prepare products for successful deployment.\n\nOur Product Engineering Capabilities\nBoard Bring-up & System Integration\nHardware & Software Integration\nSystem Verification\nProduct Performance Optimization\nProduct Qualification Support\nReliability Engineering\nProduction Readiness\nSustaining Engineering"
    ],
    [
      "Why Product Engineering Matters",
      "Why Product Engineering Matters\nValidated silicon is only one part of a successful product. Reliable deployment depends on how effectively hardware, software, and system components work together under real-world operating conditions. Product Engineering ensures this integration is achieved through structured verification, performance optimization, qualification, and readiness activities that reduce risk and improve product reliability.\n\nAs products become more sophisticated and application environments more demanding, Product Engineering plays a critical role in delivering consistent performance, accelerating deployment, and supporting long-term product success. It enables organizations to move from engineering validation to market readiness with greater confidence and predictability.\n\nKey Outcomes\nSeamless System Integration\n\nIntegrate hardware, software, and system components into a unified product that performs reliably in its intended operating environment.\n\nOptimized Product Performance\n\nRefine system behavior to achieve targeted performance, efficiency, stability, and overall product quality.\n\nImproved Product Reliability\n\nIdentify and address integration challenges early to enhance product robustness and dependable long-term operation.\n\nAccelerated Product Readiness\n\nStreamline engineering activities that prepare products for qualification, deployment, and production.\n\nReduced Deployment Risk\n\nResolve system-level issues before product release, minimizing delays, field failures, and costly engineering rework.\n\nConfident Product Realization\n\nDeliver products that are fully integrated, verified, and prepared for successful deployment through disciplined engineering execution."
    ],
    [
      "Our Product Engineering Capabilities",
      "Our Product Engineering Capabilities\nSuccessful product engineering requires more than integrating hardware and software. It involves validating system behavior, optimizing performance, supporting qualification, and preparing products for reliable deployment. Digicomm delivers comprehensive Product Engineering services that help customers bridge the gap between validated silicon and production-ready products through structured engineering execution.\n\nBoard Bring-up & Platform Enablement\n\nEnable initial hardware operation through board bring-up, platform configuration, and system initialization, establishing a stable foundation for product development.\n\nHardware & Software Integration\n\nIntegrate hardware platforms, firmware, operating systems, and application software to ensure seamless interaction across the complete product ecosystem.\n\nSystem Verification\n\nVerify end-to-end product functionality under intended operating conditions, confirming that integrated systems perform reliably and consistently.\n\nPerformance Optimization\n\nAnalyze and optimize system performance, power efficiency, responsiveness, and overall operational stability to meet product objectives.\n\nProduct Qualification Support\n\nSupport engineering qualification activities by validating product behavior against functional, environmental, and reliability requirements before deployment.\n\nReliability Engineering\n\nAssess product robustness through structured validation and reliability-focused engineering practices that enhance long-term operational performance.\n\nProduction Readiness\n\nPrepare products for manufacturing and deployment by validating engineering maturity, documentation, and release readiness.\n\nSustaining Engineering\n\nProvide ongoing engineering support for product enhancements, issue resolution, feature updates, and lifecycle maintenance following product deployment."
    ],
    [
      "Why Digicomm for Product Engineering",
      "Why Digicomm for Product Engineering\nProduct Engineering requires a coordinated approach that brings together hardware, software, system integration, validation, and performance optimization into a unified engineering effort. Digicomm combines multidisciplinary expertise with structured execution to help customers transform validated designs into reliable, deployment-ready products. Our collaborative engagement model ensures seamless integration with customer teams while maintaining a strong focus on engineering quality, product performance, and delivery confidence.\n\nWhat Sets Us Apart\nMultidisciplinary Engineering Expertise\n\nIntegrated teams with experience across hardware, embedded software, system integration, validation, and product optimization.\n\nSystem-Level Engineering Approach\n\nA holistic engineering methodology that considers the complete product ecosystem, ensuring all components work together reliably and efficiently.\n\nStructured Product Realization\n\nDisciplined engineering processes that guide products from integration and verification through qualification and production readiness.\n\nCollaborative Customer Engagement\n\nFlexible engagement models that align closely with customer engineering teams, enabling transparent communication and efficient project execution.\n\nFocus on Reliable Product Deployment\n\nAn unwavering commitment to engineering quality, system reliability, and product readiness, helping customers move confidently from development to deployment."
    ],
    [
      "Technology Expertise",
      "Technology Expertise\nSuccessful Product Engineering requires expertise across hardware platforms, embedded software, system integration, verification environments, and product qualification processes. Digicomm's engineering teams work across a broad range of technologies that enable reliable product development, seamless integration, optimized performance, and confident deployment.\n\nHardware Platforms\nDevelopment Boards\nCustom Hardware Platforms\nReference Designs\nEvaluation Kits\nPrototype Systems\nEmbedded Software & Operating Systems\nBare-Metal Firmware\nEmbedded Linux\nReal-Time Operating Systems (RTOS)\nDevice Drivers\nBoard Support Packages (BSP)\nSystem Integration\nHardware & Software Integration\nPeripheral Integration\nInterface Enablement\nSystem Configuration\nPlatform Bring-up\nProduct Verification & Qualification\nFunctional Verification\nSystem-Level Testing\nPerformance Validation\nReliability & Stress Testing\nProduct Qualification Support\nEngineering Infrastructure\nLinux Development Environments\nVersion Control Systems\nBuild & Release Automation\nIssue Tracking & Collaboration Platforms\nTechnical Documentation & Configuration Management"
    ],
    [
      "Transform Validated Designs into Deployment-Ready Products",
      "Transform Validated Designs into Deployment-Ready Products\nBuilding a successful semiconductor product requires more than innovative design and validated silicon. It demands seamless integration, disciplined engineering, and a structured approach to preparing products for real-world deployment. Digicomm Semiconductor combines multidisciplinary engineering expertise with proven execution methodologies to help customers accelerate product realization, improve system reliability, and deliver deployment-ready products with confidence.\n\nPrimary CTA\n\nDiscuss Your Product Engineering Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Semiconductor product engineering laboratory" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page product-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;

